import { ConsolidationKind as ConsolidationKindDbEnum } from "@redotech/crdb-enums/fulfillment/consolidation-kind";
import { accountIdFormat } from "@redotech/redo-account-id";
import { ConsolidationSortSchema } from "@redotech/redo-fulfillment-common/consolidation-work-queue";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import { AdvancedFilterDataSchema } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  consolidationGroupIdFormat,
  consolidationIdFormat,
} from "@redotech/redo-warehouse-id";
import * as z from "zod";

export const ConsolidationStatusFilterSchema = z.enum([
  "all",
  "active",
  "open",
  "finalizing",
  "sealed",
  "completed",
  "cancelled",
]);

export const ConsolidationOperationalStatusSchema = z.enum([
  "open",
  "finalizing",
  "sealed",
  "completed",
  "cancelled",
]);
export type ConsolidationOperationalStatus = z.infer<
  typeof ConsolidationOperationalStatusSchema
>;

export const ConsolidationKindSchema = z.enum(ConsolidationKindDbEnum);
export type ConsolidationKind = z.infer<typeof ConsolidationKindSchema>;

export const ConsolidationSummarySchema = z.object({
  closeDueAt: z.date().nullable(),
  closedAt: z.date().nullable(),
  createdAt: z.date(),
  destinationName: z.string(),
  id: consolidationIdFormat.schema,
  itemCount: z.number(),
  kind: ConsolidationKindSchema,
  laneName: z.string(),
  lastActivityAt: z.date(),
  openCartonCount: z.number(),
  originName: z.string(),
  packedItemCount: z.number(),
  participantCount: z.number(),
  participantNames: z.array(z.string()),
  sealedCartonCount: z.number(),
  status: ConsolidationOperationalStatusSchema,
  unpackedItemCount: z.number(),
  voidedCartonCount: z.number(),
});
export type ConsolidationSummary = z.infer<typeof ConsolidationSummarySchema>;
export type ConsolidationStatusFilter = z.infer<
  typeof ConsolidationStatusFilterSchema
>;

const inputBase = {
  advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
  filter: ConsolidationStatusFilterSchema,
  search: z.string().trim().max(200).nullish(),
};

export const listConsolidations = {
  name: "listConsolidations" as const,
  input: z.object({
    ...inputBase,
    consolidationId: consolidationIdFormat.schema.optional(),
    pageNumber: z.number().int().min(0).default(0),
    pageSize: z.number().int().min(1).max(100).default(25),
    sort: ConsolidationSortSchema.optional(),
  }),
  output: z.object({ consolidations: z.array(ConsolidationSummarySchema) }),
};

export const sealConsolidation = {
  name: "sealConsolidation" as const,
  input: z.object({ consolidationId: consolidationIdFormat.schema }),
  output: z.object({ sealedAt: z.date() }),
};

export const fulfillConsolidation = {
  name: "fulfillConsolidation" as const,
  input: z.object({ consolidationId: consolidationIdFormat.schema }),
  output: z.object({ completedAt: z.date() }),
};

export const getConsolidationStatusCounts = {
  name: "getConsolidationStatusCounts" as const,
  input: z.object(inputBase),
  output: z.object({
    active: z.number(),
    cancelled: z.number(),
    closeDue: z.number(),
    completed: z.number(),
    finalizing: z.number(),
    open: z.number(),
    readyToShip: z.number(),
    sealed: z.number(),
    total: z.number(),
  }),
};

const ConsolidationLocationFilterOptionSchema = z.object({
  label: z.string(),
  value: locationIdFormat.schema,
});

export const getConsolidationFilterOptions = {
  name: "getConsolidationFilterOptions" as const,
  input: z.object({}),
  output: z.object({
    destinations: z.array(ConsolidationLocationFilterOptionSchema),
    lanes: z.array(
      z.object({ label: z.string(), value: consolidationGroupIdFormat.schema }),
    ),
    merchants: z.array(
      z.object({ label: z.string(), value: accountIdFormat.schema }),
    ),
    origins: z.array(ConsolidationLocationFilterOptionSchema),
  }),
};
